import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FoodCard from "@/components/base/FoodCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { foods, getFoodById } from "@/mocks/foods";
import Gallery from "./components/Gallery";
import OrderPanel from "./components/OrderPanel";
import Reviews from "./components/Reviews";

export default function FoodDetail() {
  const { id } = useParams<{ id: string }>();
  const food = id ? getFoodById(id) : undefined;

  usePageMeta(
    food ? `${food.name}详情 - 美味的快递` : "美食详情 - 美味的快递",
    food
      ? `${food.name}，${food.short}。价格 ¥${food.price} 起，评分 ${food.rating} 分，由 ${food.restaurant.name} 出品，美味的快递极速送达。`
      : "查看美味的快递美食详情，精选严选商家餐品，极速配送到家。",
    food ? `${food.name},${food.category},美味的快递,美食外卖,在线点餐` : "美味的快递,美食详情,美食外卖"
  );

  if (!food) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-emotion-sad-line text-4xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-extrabold text-foreground-950">没有找到这道美食</h1>
        <p className="mt-3 text-sm text-foreground-500">它可能已下架，或链接有误，去美食分类看看别的吧。</p>
        <Link
          to="/food-category"
          className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600"
        >
          <i className="ri-restaurant-2-line" />
          返回美食分类
        </Link>
      </div>
    );
  }

  const related = foods.filter((f) => f.categoryId === food.categoryId && f.id !== food.id).slice(0, 3);
  const fallback = foods.filter((f) => f.id !== food.id).slice(0, 3);
  const recommends = related.length >= 3 ? related : fallback;

  return (
    <div className="bg-background-50">
      <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "美食分类", to: "/food-category" },
            { label: food.category, to: "/food-category" },
            { label: food.name },
          ]}
        />
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 pb-14 md:px-8 md:pb-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          <Gallery images={food.gallery} name={food.name} />
          <OrderPanel food={food} />
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 lg:col-span-2">
            <h2 className="font-heading text-xl font-extrabold text-foreground-950">美食介绍</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{food.description}</p>

            <h3 className="mt-7 font-heading text-base font-bold text-foreground-950">营养信息（每份约）</h3>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {food.nutrition.map((n) => (
                <div key={n.label} className="rounded-lg bg-background-100 p-4 text-center">
                  <p className="font-heading text-lg font-extrabold text-primary-600">{n.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{n.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading text-lg font-extrabold text-foreground-950">商家信息</h2>
            <div className="mt-4 flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className="ri-store-2-line text-xl" />
              </span>
              <div>
                <p className="font-heading text-sm font-bold text-foreground-950">{food.restaurant.name}</p>
                <p className="mt-1 text-xs text-foreground-500">{food.restaurant.distance}</p>
              </div>
            </div>
            <ul className="mt-5 flex flex-col gap-3 text-sm">
              <li className="flex items-center gap-2 text-foreground-700">
                <i className="ri-phone-line text-accent-600" />
                {food.restaurant.phone}
              </li>
              <li className="flex items-center gap-2 text-foreground-700">
                <i className="ri-timer-flash-line text-accent-600" />
                预计送达 {food.restaurant.deliveryTime}
              </li>
              <li className="flex items-center gap-2 text-foreground-700">
                <i className="ri-shield-check-line text-accent-600" />
                平台担保 · 不新鲜包退
              </li>
            </ul>
            <Link
              to="/courier-send"
              className="mt-6 flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-accent-300 bg-accent-50 px-5 py-2.5 text-sm font-bold text-accent-700 transition hover:bg-accent-100"
            >
              <i className="ri-send-plane-fill" />
              需要帮送其他物品？
            </Link>
          </div>
        </div>

        <Reviews />

        <section className="mt-14">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">相关推荐</h2>
            <Link to="/food-category" className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
              更多美食
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div data-product-shop className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recommends.map((f) => (
              <FoodCard key={f.id} food={f} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}